/**
 * PAD Health Checks report cleanup helpers.
 *
 * Why this exists
 * ---------------
 * The "Carry out health checks now?" gate (Treatment Options 3.3.Q2, also shown
 * on Strategies in SLaM treatment-first order) correctly *prevents* navigating
 * into Health Checks when the answer is "No - defer". Generation for Health
 * Checks only runs from the Health Checks page itself, so a clean defer path
 * never creates pad_HLT_* paragraphs.
 *
 * The bug this fixes: a clinician can select Yes, visit Health Checks (which
 * generates LLM paragraphs), then go back and change the answer to
 * "No - defer". Until now that only re-locked the sidebar — it left the
 * already-generated pad_HLT_* (and any AMP) outputs / approvals in
 * 6_manageReports. RD_PAD and the CAR are content-gated, so those stale
 * paragraphs still appeared in the report.
 *
 * Call `buildClearDeferredHealthChecksReportUpdate` whenever the gate leaves
 * Yes, and merge the result into the same updatePatientData write as the new
 * answer so the clear is atomic with the deferral.
 */

/** Paragraph codes generated by the Health Checks PAD section. */
export const PAD_HEALTH_CHECKS_PARAGRAPH_CODES = Object.freeze([
  'pad_HLT_weight',
  'pad_HLT_blood_pressure',
  'pad_HLT_pulse',
  'pad_HLT_medical_history_review',
  'pad_HLT_adhd_controlled_drug_prescribing',
  'pad_HLT_outcome_of_health_checks',
]);

/**
 * Agreed Medication Plan codes that can only exist after Health Checks.
 * Cleared together with Health Checks so a Yes→HLT→AMP→No-defer path cannot
 * leave orphan medication paragraphs in the report either.
 */
export const PAD_AMP_PARAGRAPH_CODES = Object.freeze([
  'pad_AMP_agreed_medication_plan',
  'pad_AMP_titration_schedule',
  'pl_medicationPlanWeek1',
  'pl_medicationPlanWeek2',
  'pl_medicationPlanWeek3',
  'pl_medicationPlanWeek4',
]);

const DEFERRED_SECTION_TITLES = Object.freeze([
  'Health Checks',
  'Agreed Medication Plan',
]);

const CODES_TO_CLEAR = Object.freeze([
  ...PAD_HEALTH_CHECKS_PARAGRAPH_CODES,
  ...PAD_AMP_PARAGRAPH_CODES,
]);

/**
 * Strip Health Checks (+ AMP) keys from a report map (outputs / approvals /
 * inputs). Returns a new object; never mutates the input.
 *
 * @param {Object|null|undefined} map
 * @returns {Object}
 */
function omitDeferredCodes(map) {
  const next = { ...(map || {}) };
  for (const code of CODES_TO_CLEAR) {
    delete next[code];
  }
  return next;
}

/**
 * Whether the PAD report section currently holds any Health Checks / AMP
 * artefact that should be wiped when the clinician defers health checks.
 *
 * @param {Object|null|undefined} padReportSection - manageReports.sections['4']
 * @returns {boolean}
 */
export function hasDeferredHealthChecksReportContent(padReportSection) {
  if (!padReportSection) return false;

  const generated = padReportSection.stageSectionsGenerated || [];
  if (generated.some((title) => DEFERRED_SECTION_TITLES.includes(title))) {
    return true;
  }

  for (const map of [
    padReportSection.outputs,
    padReportSection.approvedParagraphs,
    padReportSection.inputs,
  ]) {
    if (!map) continue;
    for (const code of CODES_TO_CLEAR) {
      const value = map[code];
      if (value !== undefined && value !== null && value !== '') {
        return true;
      }
    }
  }

  return false;
}

/**
 * Build a nested updatePatientData payload that removes Health Checks (+ AMP)
 * report content from 6_manageReports.sections.4.
 *
 * Uses deepMerge's `{ condition, value }` replace shape for the maps so keys
 * are actually deleted (plain deep-merge would leave stale pad_HLT_* entries).
 * Arrays are replaced wholesale by deepMerge, so filtering stageSectionsGenerated
 * is enough.
 *
 * Returns null when there is nothing to clear (avoids a no-op write).
 *
 * @param {Object|null} patientDataModel
 * @returns {Object|null} `{ '6_manageReports': { sections: { '4': ... } } }` or null
 */
export function buildClearDeferredHealthChecksReportUpdate(patientDataModel) {
  const padReportSection =
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4'] || null;

  if (!hasDeferredHealthChecksReportContent(padReportSection)) {
    return null;
  }

  const filteredGenerated = (padReportSection.stageSectionsGenerated || []).filter(
    (title) => !DEFERRED_SECTION_TITLES.includes(title)
  );

  // `{ condition: () => true, value }` tells PatientDataContext's deepMerge to
  // replace the whole map rather than merge keys — otherwise deleted pad_HLT_*
  // codes would survive from the existing Firestore document.
  const replaceMap = (map) => ({
    condition: () => true,
    value: omitDeferredCodes(map),
  });

  return {
    '6_manageReports': {
      sections: {
        '4': {
          outputs: replaceMap(padReportSection.outputs),
          approvedParagraphs: replaceMap(padReportSection.approvedParagraphs),
          inputs: replaceMap(padReportSection.inputs),
          stageSectionsGenerated: filteredGenerated,
        },
      },
    },
  };
}

/**
 * True when the clinician's new gate selection means Health Checks should not
 * be part of the live PAD / report (anything other than an affirmative Yes).
 *
 * @param {string[]|null|undefined} answerValue - answer.value array
 * @returns {boolean}
 */
export function isHealthChecksGateDeferred(answerValue) {
  if (!Array.isArray(answerValue) || answerValue.length === 0) return true;
  return !answerValue.includes('Yes');
}
